/**
 * Quant Mode API Service
 *
 * Provides API integration for the Quant Mode monitoring dashboard,
 * which lists all Crashing Out runs across matters.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QuantModeRun {
  run_id: string;
  workflow_id: string;
  matter_id: string;
  matter_name: string;
  coverage_id: string;
  coverage_name: string;
  coverage_category: string;
  status: string;
  started_at: string;
  closed_at: string | null;
  initiated_by_user_id: string | null;
  initiated_by_user_name: string | null;
  final_response: string | null;
  error: string | null;
  latest_tool: string | null;
  latest_tool_status: string | null;
}

export interface QuantModeRunsParams {
  status?: string;
  user_id?: string;
  task_assignee_id?: string;
  started_after?: string;
  started_before?: string;
  limit?: number;
  offset?: number;
  source?: 'temporal' | 'postgres';
}

export interface PaginatedQuantModeRuns {
  count: number;
  next: string | null;
  previous: string | null;
  results: QuantModeRun[];
}

// ---------------------------------------------------------------------------
// API Service
// ---------------------------------------------------------------------------

export const quantModeApiClient = createApi({
  reducerPath: 'quantModeApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['QuantModeRuns'] as const,
  endpoints: (builder) => ({
    /**
     * List Crashing Out runs across all matters.
     * Supports filtering by status, user, date range, and pagination.
     */
    getQuantModeRuns: builder.query<PaginatedQuantModeRuns, QuantModeRunsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.set('status', params.status);
        if (params.user_id) searchParams.set('user_id', params.user_id);
        if (params.task_assignee_id) searchParams.set('task_assignee_id', params.task_assignee_id);
        if (params.started_after) searchParams.set('started_after', params.started_after);
        if (params.started_before) searchParams.set('started_before', params.started_before);
        if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
        if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
        if (params.source) searchParams.set('source', params.source);
        const qs = searchParams.toString();
        return `api/agentflow/crashing-out/runs/${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['QuantModeRuns'],
    }),
  }),
});

export const { useGetQuantModeRunsQuery } = quantModeApiClient;
